// Public blog index — lists all published SEO articles.
// Route: /blog
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import axios from "axios";
import { API } from "../apiConfig";

export default function BlogIndex() {
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    document.title = "Blog — Oly Payroll";
    axios.get(`${API}/marketing/articles`)
      .then((r) => setArticles(r.data.items || []))
      .catch(() => setArticles([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#0A1F3B] text-white">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-[#0A1F3B]/80 backdrop-blur-xl border-b border-[#1C3D66]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-1">
            <span className="font-heading text-2xl font-light text-[#F5D300]">oly</span>
            <span className="font-heading text-2xl font-black text-white">Payroll</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/blog" className="text-[#F5D300] text-sm font-medium">Blog</Link>
            <Link to="/auth" className="text-[#94A3B8] hover:text-white text-sm font-medium transition-colors">Sign In</Link>
            <Link to="/auth?mode=register" className="bg-[#F5D300] text-black font-bold rounded-lg px-5 py-2.5 text-sm hover:-translate-y-0.5 transition-all duration-300">
              Start Free
            </Link>
          </div>
        </div>
      </nav>

      {/* Header */}
      <header className="py-16 sm:py-20 px-6 sm:px-8 border-b border-[#1C3D66]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#F5D300] mb-4">Oly Payroll Blog</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4" data-testid="blog-index-title">
            Payroll tips, guides & compliance updates
          </h1>
          <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto">
            Plain-English advice on overtime rules, payroll prep, time clock management, and getting your shift data into Gusto, ADP, or QuickBooks — without the headache.
          </p>
        </div>
      </header>

      {/* Articles grid */}
      <main className="py-12 sm:py-16 px-6 sm:px-8">
        <div className="max-w-5xl mx-auto">
          {articles === null ? (
            <p className="text-center text-[#94A3B8]">Loading articles...</p>
          ) : articles.length === 0 ? (
            <p className="text-center text-[#94A3B8]" data-testid="blog-empty">No articles published yet. Check back soon!</p>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="blog-article-grid">
              {articles.map((a) => (
                <Link
                  key={a._id}
                  to={`/blog/${a.slug}`}
                  className="group bg-[#112A4A] border border-[#1C3D66] rounded-xl p-6 hover:border-[#F5D300]/40 transition-all duration-300"
                  data-testid={`blog-card-${a.slug}`}
                >
                  {a.angle && (
                    <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-[#F5D300] mb-3">
                      {a.angle.replace(/-/g, " ")}
                    </span>
                  )}
                  <h2 className="font-heading text-lg font-bold text-white mb-2 group-hover:text-[#F5D300] transition-colors leading-snug">
                    {a.title}
                  </h2>
                  {a.meta_description && (
                    <p className="text-sm text-[#94A3B8] mb-3 line-clamp-3">{a.meta_description}</p>
                  )}
                  <div className="flex items-center justify-between text-xs text-[#475569]">
                    <span>{a.audience}</span>
                    {a.word_count > 0 && <span>{a.word_count} words</span>}
                  </div>
                  <div className="mt-4 flex items-center gap-1 text-sm text-[#F5D300] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    Read article <ArrowRight className="w-4 h-4" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#1C3D66] py-8 px-6 sm:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-1">
            <span className="font-heading text-lg font-light text-[#F5D300]">oly</span>
            <span className="font-heading text-lg font-black text-white">Payroll</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-[#475569]">
            <Link to="/">Home</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
